import type { Background } from '@character-creator/types';
import { z } from 'zod';
import { BackgroundSchema } from '../schemas';
import { ignusBackgrounds } from '../rulesets/ignus';

/**
 * SRD 5.1 background data
 * Only Acolyte is included in the SRD
 */
const srdBackgrounds: Background[] = [
  {
    id: 'acolyte',
    name: 'Acolyte',
    description:
      'You have spent your life in the service of a temple to a specific god or pantheon of gods. You act as an intermediary between the realm of the holy and the mortal world, performing sacred rites and offering sacrifices in order to conduct worshipers into the presence of the divine.',
    skillProficiencies: ['insight', 'religion'],
    toolProficiencies: [],
    languages: 2,
    equipment: [
      'A holy symbol',
      'A prayer book or prayer wheel',
      '5 sticks of incense',
      'Vestments',
      'A set of common clothes',
      'A pouch containing 15 gp',
    ],
    feature: {
      name: 'Shelter of the Faithful',
      description:
        'As an acolyte, you command the respect of those who share your faith, and you can perform the religious ceremonies of your deity. You and your adventuring companions can expect to receive free healing and care at a temple, shrine, or other established presence of your faith, though you must provide any material components needed for spells. Those who share your religion will support you (but only you) at a modest lifestyle.',
    },
    suggestedCharacteristics: {
      personalityTraits: [
        'I idolize a particular hero of my faith, and constantly refer to that person\'s deeds and example.',
        'I can find common ground between the fiercest enemies, empathizing with them and always working toward peace.',
        'I see omens in every event and action. The gods try to speak to us, we just need to listen.',
        'Nothing can shake my optimistic attitude.',
        'I quote (or misquote) sacred texts and proverbs in almost every situation.',
        'I am tolerant (or intolerant) of other faiths and respect (or condemn) the worship of other gods.',
        'I\'ve enjoyed fine food, drink, and high society among my temple\'s elite. Rough living grates on me.',
        'I\'ve spent so long in the temple that I have little practical experience dealing with people in the outside world.',
      ],
      ideals: [
        'Tradition. The ancient traditions of worship and sacrifice must be preserved and upheld. (Lawful)',
        'Charity. I always try to help those in need, no matter what the personal cost. (Good)',
        'Change. We must help bring about the changes the gods are constantly working in the world. (Chaotic)',
        'Power. I hope to one day rise to the top of my faith\'s religious hierarchy. (Lawful)',
        'Faith. I trust that my deity will guide my actions. I have faith that if I work hard, things will go well. (Lawful)',
        'Aspiration. I seek to prove myself worthy of my god\'s favor by matching my actions against his or her teachings. (Any)',
      ],
      bonds: [
        'I would die to recover an ancient relic of my faith that was lost long ago.',
        'I will someday get revenge on the corrupt temple hierarchy who branded me a heretic.',
        'I owe my life to the priest who took me in when my parents died.',
        'Everything I do is for the common people.',
        'I will do anything to protect the temple where I served.',
        'I seek to preserve a sacred text that my enemies consider heretical and seek to destroy.',
      ],
      flaws: [
        'I judge others harshly, and myself even more severely.',
        'I put too much trust in those who wield power within my temple\'s hierarchy.',
        'My piety sometimes leads me to blindly trust those that profess faith in my god.',
        'I am inflexible in my thinking.',
        'I am suspicious of strangers and expect the worst of them.',
        'Once I pick a goal, I become obsessed with it to the detriment of everything else in my life.',
      ],
    },
    source: 'srd-5.1',
  },
];

/**
 * Combine all background sources
 */
const rawBackgrounds: Background[] = [...srdBackgrounds, ...ignusBackgrounds];

/**
 * Validate all background data at module load time
 */
function validateBackgrounds(data: Background[]): Background[] {
  const BackgroundArraySchema = z.array(BackgroundSchema);
  const result = BackgroundArraySchema.safeParse(data);

  if (!result.success) {
    const formatted = JSON.stringify(result.error.format(), null, 2);
    throw new Error(`Background data validation failed:\n${formatted}`);
  }

  return data;
}

export const backgrounds: Background[] = validateBackgrounds(rawBackgrounds);
